import '@/assets/styles/main.scss';
import { glsClearLocalStorageWhenAppVersionChange } from '@gls-its-frontends/common/gls-app-config';
import { glsVueQueryPluginOptions } from '@gls-its-frontends/common/gls-query';
import { glsCreateI18nInstance } from '@gls-its-frontends/common/gls-translate';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia, type Pinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { type App, createApp, type Plugin } from 'vue';
import de from '@/assets/i18n/de.json';
import en from '@/assets/i18n/en.json';
import { LOCAL_STORAGE_PREFIX } from '@/constants/local-storage-prefix';
import FeatureEnabledDirective from '@/directives/v-feature-flag-enabled';
import PermissionDirective from '@/directives/v-permission';
import { Environment } from '@/enums/environment';
import { getEnvironment } from '@/helpers/get-environment';
import { injectRobotsMetaTag } from '@/helpers/robots';
import { setAppTitle } from '@/helpers/set-app-title';
import { setUpDynatrace } from '@/helpers/set-up-dynatrace';
import { setUpEnvironmentConfig } from '@/helpers/set-up-environment-config';
import { FeatureFlagsService } from '@/services/feature-flags-service';

/**
 * The app is wrapped into an async due to the necessity of fetching
 * the environment variable 'env' before other env-dependent imports happen.
 * Only the fetch function needs an async call, but as the dependent imports need
 * to execute afterwards, those have to be in await syntax as well.
 * The top level async is wrapped into an IFFE, otherwise the app wouldn't execute
 * on startup.
 */
(async (): Promise<void> => {
    const environment: Environment = getEnvironment();

    await FeatureFlagsService.init();
    glsClearLocalStorageWhenAppVersionChange(APP_VERSION, LOCAL_STORAGE_PREFIX);

    setUpEnvironmentConfig(environment);
    setUpDynatrace(environment);
    setAppTitle(environment);

    if (environment !== Environment.PROD) {
        injectRobotsMetaTag();
    }

    const { default: App } = await import('@/App.vue');
    const { default: router } = await import('@/routing/router');

    const pinia: Pinia = createPinia().use(piniaPluginPersistedstate);
    const i18n: Plugin = glsCreateI18nInstance({ de, en });

    const app: App<Element> = createApp(App);
    app.directive('permission', PermissionDirective)
        .directive('feature-flag-enabled', FeatureEnabledDirective)
        .use(pinia)
        .use(i18n)
        .use(router)
        .use(VueQueryPlugin, glsVueQueryPluginOptions)
        .mount('#app');
})();
